import { formatDate } from '@app/src/components/Form/ControlledDateField'
import { useResponseItemContext } from '@app/src/context/ResponseItemContext'
import { AutoAwesomeOutlined, HistoryOutlined } from '@mui/icons-material'
import { Button, Link, Stack, Typography } from '@mui/material'
import type { FC, ReactNode } from 'react'
import React from 'react'
import { useIntl } from 'react-intl'
import type { AppliedSuggestion } from './suggestionSources'
import { isAiSource, SuggestionSource } from './suggestionSources'

type AppliedSuggestionNoteProps = {
  appliedSuggestion: AppliedSuggestion
  onView: (source: SuggestionSource) => void
}

const AppliedSuggestionNote: FC<AppliedSuggestionNoteProps> = ({ appliedSuggestion, onView }) => {
  const { formatMessage } = useIntl()
  const { previousAnswer } = useResponseItemContext()

  const isFromAi = isAiSource(appliedSuggestion.source)
  const openDrawer = () => onView(appliedSuggestion.source)

  const sourceLink = (label: string, key?: string) => (
    <Link key={key ?? label} underline="always" color="inherit" onClick={openDrawer} sx={{ cursor: 'pointer' }}>
      {label}
    </Link>
  )

  const fileNameLinks = appliedSuggestion.fileNames?.map((fileName, index) => (
    <React.Fragment key={fileName}>
      {sourceLink(fileName)}
      {index < (appliedSuggestion.fileNames?.length ?? 0) - 1 && ', '}
    </React.Fragment>
  ))

  const description: ReactNode = (() => {
    switch (appliedSuggestion.source) {
      case SuggestionSource.Document:
        return formatMessage(
          { id: 'suggestedAnswers.appliedFromDocuments' },
          {
            fileNames: fileNameLinks?.length ? fileNameLinks : formatMessage({ id: 'suggestedAnswers.yourDocuments' }),
          },
        )
      case SuggestionSource.PublicData:
        return formatMessage({ id: 'suggestedAnswers.appliedFromPublicData' })
      default:
        return formatMessage(
          { id: 'suggestedAnswers.appliedFromPreviousAnswer' },
          {
            requestName: previousAnswer?.response?.request?.title
              ? sourceLink(previousAnswer.response.request.title, 'requestName')
              : '',
            date: previousAnswer?.response?.submittedAt ? formatDate(previousAnswer.response.submittedAt, true) : '',
          },
        )
    }
  })()

  return (
    <Stack
      direction="row"
      spacing={1.5}
      alignItems="center"
      px={2}
      py={2}
      bgcolor={isFromAi ? 'communication.light' : 'success.light'}
    >
      {isFromAi ? (
        <AutoAwesomeOutlined fontSize="small" sx={{ color: 'communication.dark', flexShrink: 0 }} />
      ) : (
        <HistoryOutlined fontSize="small" sx={{ flexShrink: 0 }} />
      )}
      <Typography noWrap flexGrow={1} minWidth={0}>
        {description}
      </Typography>
      <Button size="small" onClick={openDrawer} sx={{ flexShrink: 0 }}>
        {formatMessage({ id: 'general.view' })}
      </Button>
    </Stack>
  )
}

export default AppliedSuggestionNote
